import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { updateUser, changePasswordApi, changeEmailApi, ecpChallengeApi, ecpBindApi, getConsentMe } from "../api";
import { ROLES } from "../constants";
import { signChallengeWithEcp, ecpErrorMessage } from "../lib/ncalayer";
import { normalizeApiError } from "../lib/apiError";
import AppPage from "../components/AppPage";
import { sanitizeProfilePhone, PROFILE_PHONE_MAX_LEN } from "../utils/phone";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Resize image to max 320x320, output JPEG ~0.85 quality. Returns data URL.
async function resizeImage(file, maxSize = 320, quality = 0.85) {
  const dataUrl = await fileToBase64(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > height) {
        if (width > maxSize) { height = Math.round(height * maxSize / width); width = maxSize; }
      } else {
        if (height > maxSize) { width = Math.round(width * maxSize / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      try { resolve(canvas.toDataURL("image/jpeg", quality)); }
      catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [position, setPosition] = useState(user?.position || "");
  const [photo, setPhoto] = useState(user?.photo_b64 || user?.picture || "");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [iin, setIin] = useState(user?.iin || "");
  const [birthDate, setBirthDate] = useState(user?.birth_date || "");
  const [gender, setGender] = useState(user?.gender || "");
  const [phone, setPhone] = useState(() => sanitizeProfilePhone(user?.phone || ""));
  const [address, setAddress] = useState(user?.address || "");
  const [emergencyContact, setEmergencyContact] = useState(user?.emergency_contact || "");
  const [bankAccount, setBankAccount] = useState(user?.bank_account || "");
  const [bankName, setBankName] = useState(user?.bank_name || "");
  const [education, setEducation] = useState(user?.education || "");
  const [notes, setNotes] = useState(user?.notes || "");
  const [vacationDaysLimit, setVacationDaysLimit] = useState(
    user?.vacation_days_limit != null ? String(user.vacation_days_limit) : ""
  );

  const isHrAdmin = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);

  const localeMap = { ru: "ru-RU", kk: "kk-KZ", en: "en-US" };
  const numLocale = localeMap[i18n.language] || "ru-RU";

  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr(t("profile.photo_only_image")); return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setErr(t("profile.photo_too_big")); return;
    }
    setBusy(true); setErr(""); setMsg("");
    try {
      const b64 = await resizeImage(file, 320, 0.85);
      await updateUser(user.user_id, { photo_b64: b64 });
      setPhoto(b64);
      await refresh();
      setMsg(t("profile.photo_updated"));
    } catch {
      setErr(t("profile.photo_upload_error"));
    } finally { setBusy(false); e.target.value = ""; }
  };

  const onRemovePhoto = async () => {
    if (!window.confirm(t("profile.remove_photo_confirm"))) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await updateUser(user.user_id, { photo_b64: "" });
      setPhoto("");
      await refresh();
      setMsg(t("profile.photo_removed"));
    } catch { setErr(t("common.error")); }
    finally { setBusy(false); }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      await updateUser(user.user_id, {
        name, position,
        iin, birth_date: birthDate, gender, phone, address,
        emergency_contact: emergencyContact,
        bank_account: bankAccount, bank_name: bankName,
        education, notes,
      });
      await refresh();
      setMsg(t("profile.saved"));
    } catch {
      setErr(t("profile.save_error"));
    } finally { setBusy(false); }
  };

  return (
    <AppPage testId="profile-page" bodyScroll="scroll">
      <h4 className="fw-bold mb-4">{t("profile.title")}</h4>
      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {err && <div className="alert alert-danger py-2">{err}</div>}

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card card-soft text-center"><div className="card-body">
            <div className="mb-3">
              {photo ? (
                <img src={photo} alt="" className="rounded-circle" width="120" height="120" style={{ objectFit: "cover" }} />
              ) : (
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center"
                     style={{ width: 120, height: 120, background: "#e9ecf2" }}>
                  <i className="bi bi-person fs-1 text-muted"></i>
                </div>
              )}
            </div>
            <label className="btn btn-outline-primary btn-sm" data-testid="photo-upload-btn">
              <i className="bi bi-upload me-1"></i>{photo ? t("profile.replace_photo") : t("profile.upload_photo")}
              <input type="file" accept="image/*" hidden onChange={onUploadPhoto} disabled={busy} />
            </label>
            {photo && (
              <button className="btn btn-link btn-sm text-danger ms-2"
                      onClick={onRemovePhoto} disabled={busy} data-testid="photo-remove-btn">
                {t("profile.remove_photo")}
              </button>
            )}
            <div className="small text-muted mt-2" style={{ fontSize: 11 }}>
              {t("profile.photo_resize_hint")}
            </div>
            <div className="mt-3 small text-muted">
              {t("profile.salary_info", {
                role: t(`roles.${user?.role}`, { defaultValue: "" }),
                salary: Number(user?.salary || 0).toLocaleString(numLocale),
              })}
            </div>
            <div className="mt-2 small">
              <div className="text-muted">{t("profile.vacation_days")}</div>
              {isHrAdmin ? (
                <input type="number" className="form-control form-control-sm mt-1" style={{ maxWidth: 100 }}
                       min={24} placeholder="24"
                       value={vacationDaysLimit}
                       onChange={(e) => setVacationDaysLimit(e.target.value)}
                       onBlur={async () => {
                         const raw = (vacationDaysLimit || "").trim();
                         const n = raw === "" ? null : Number(raw);
                         if (raw !== "" && (!Number.isFinite(n) || n < 24)) {
                           setErr(t("profile.vacation_days_min"));
                           return;
                         }
                         setBusy(true); setErr(""); setMsg("");
                         try {
                           await updateUser(user.user_id, { vacation_days_limit: n });
                           await refresh();
                           setMsg(t("profile.saved"));
                         } catch { setErr(t("profile.save_error")); }
                         finally { setBusy(false); }
                       }}
                       data-testid="profile-vacation-days" />
              ) : (
                <div className="fw-semibold">
                  {user?.vacation_days_limit ?? 24}
                  {user?.vacation_days_limit == null && (
                    <span className="text-muted fw-normal"> ({t("profile.vacation_days_default")})</span>
                  )}
                </div>
              )}
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>{t("profile.vacation_days_hint")}</div>
            </div>
          </div></div>
        </div>

        <div className="col-md-8">
          <div className="card card-soft mb-3"><div className="card-body">
            <h6 className="fw-semibold mb-3">{t("profile.personal_data")}</h6>
            <form onSubmit={onSave}>
              <div className="mb-2">
                <label className="form-label small">{t("common.fullname")}</label>
                <input className="form-control" value={name}
                       onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
              </div>
              <div className="mb-3">
                <label className="form-label small">{t("common.position")}</label>
                <input className="form-control" value={position}
                       onChange={(e) => setPosition(e.target.value)} data-testid="profile-position" />
              </div>

              <hr className="my-3" />
              <h6 className="fw-semibold mb-3">
                <i className="bi bi-card-list me-2"></i>{t("profile.section_personal")}
                <small className="text-muted ms-2 fw-normal" style={{ fontSize: 12 }}>
                  {t("profile.all_optional")}
                </small>
              </h6>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.iin")}</label>
                  <input className="form-control" value={iin}
                         onChange={(e) => setIin(e.target.value)}
                         placeholder={t("profile.iin_ph")} maxLength={12}
                         data-testid="profile-iin" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.birth_date")}</label>
                  <input type="date" className="form-control" value={birthDate}
                         onChange={(e) => setBirthDate(e.target.value)}
                         data-testid="profile-birth" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.gender")}</label>
                  <select className="form-select" value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          data-testid="profile-gender">
                    <option value="">{t("profile.gender_not_set")}</option>
                    <option value="male">{t("profile.gender_male")}</option>
                    <option value="female">{t("profile.gender_female")}</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.phone")}</label>
                  <input type="tel" className="form-control" value={phone}
                         onChange={(e) => setPhone(sanitizeProfilePhone(e.target.value))}
                         maxLength={PROFILE_PHONE_MAX_LEN}
                         inputMode="tel"
                         autoComplete="tel"
                         placeholder={t("profile.phone_ph")}
                         data-testid="profile-phone" />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("profile.address")}</label>
                  <input className="form-control" value={address}
                         onChange={(e) => setAddress(e.target.value)}
                         data-testid="profile-address" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.education")}</label>
                  <input className="form-control" value={education}
                         onChange={(e) => setEducation(e.target.value)}
                         placeholder={t("profile.education_ph")}
                         data-testid="profile-education" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.emergency")}</label>
                  <input className="form-control" value={emergencyContact}
                         onChange={(e) => setEmergencyContact(e.target.value)}
                         placeholder={t("profile.emergency_ph")}
                         data-testid="profile-emergency" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.bank_name")}</label>
                  <input className="form-control" value={bankName}
                         onChange={(e) => setBankName(e.target.value)}
                         placeholder={t("profile.bank_name_ph")}
                         data-testid="profile-bankname" />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">{t("profile.bank_account")}</label>
                  <input className="form-control" value={bankAccount}
                         onChange={(e) => setBankAccount(e.target.value)}
                         placeholder={t("profile.bank_account_ph")}
                         data-testid="profile-bankaccount" />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t("profile.notes")}</label>
                  <textarea className="form-control" rows={2} value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="profile-notes" />
                </div>
              </div>

              <button className="btn btn-primary" disabled={busy} data-testid="profile-save">{t("profile.save")}</button>
            </form>
          </div></div>

          <div className="card card-soft"><div className="card-body">
            <h6 className="fw-semibold mb-3">{t("profile.section_password")}</h6>
            <PasswordChangeForm />
          </div></div>

          <div className="card card-soft mt-3"><div className="card-body">
            <h6 className="fw-semibold mb-3">{t("profile.section_email")}</h6>
            <EmailChangeForm />
          </div></div>

          <div className="card card-soft mt-3"><div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-shield-lock me-1"></i>
              ЭЦП НУЦ РК
            </h6>
            <EcpBindSection />
          </div></div>

          <div className="card card-soft mt-3"><div className="card-body">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-file-text me-1"></i>
              {t("legal.profile_title")}
            </h6>
            <LegalInfoSection />
          </div></div>
        </div>
      </div>
    </AppPage>
  );
}

function EmailChangeForm() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [newEmail, setNewEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (!newEmail || newEmail === user?.email) {
      setErr(t("profile.email_change_invalid"));
      return;
    }
    if (!window.confirm(t("profile.email_change_confirm", { old: user?.email, new: newEmail }))) return;
    setBusy(true);
    try {
      await changeEmailApi(newEmail, pwd || "");
      if (refresh) await refresh();
      setMsg(t("profile.email_changed", { email: newEmail }));
      setNewEmail(""); setPwd("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("profile.email_change_error"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={onSubmit} className="row g-2" data-testid="email-change-form">
      {msg && <div className="col-12"><div className="alert alert-success py-2 mb-0">{msg}</div></div>}
      {err && <div className="col-12"><div className="alert alert-danger py-2 mb-0">{err}</div></div>}
      <div className="col-12">
        <div className="small text-muted mb-2">
          {t("profile.current_email")}: <strong>{user?.email}</strong>
        </div>
      </div>
      <div className="col-md-5">
        <input type="email" className="form-control" placeholder={t("profile.new_email")} required
               value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
               data-testid="profile-newemail" />
      </div>
      <div className="col-md-5">
        <input type="password" className="form-control" placeholder={t("profile.current_password_for_email")}
               value={pwd} onChange={(e) => setPwd(e.target.value)}
               data-testid="profile-emailpwd" />
      </div>
      <div className="col-md-2">
        <button className="btn btn-outline-primary w-100" disabled={busy}
                data-testid="profile-changeemail">
          {busy ? "…" : t("profile.change")}
        </button>
      </div>
      <div className="col-12">
        <small className="text-muted">{t("profile.google_email_hint")}</small>
      </div>
    </form>
  );
}

function PasswordChangeForm() {
  const { t } = useTranslation();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (newPwd !== newPwd2) { setErr(t("profile.passwords_mismatch")); return; }
    if (newPwd.length < 6) { setErr(t("profile.min_6_chars")); return; }
    setBusy(true);
    try {
      await changePasswordApi(oldPwd || null, newPwd);
      setOldPwd(""); setNewPwd(""); setNewPwd2("");
      setMsg(t("profile.password_changed"));
    } catch (e2) {
      setErr(e2.response?.data?.detail || t("profile.password_error"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={onSubmit} className="row g-2">
      {msg && <div className="col-12"><div className="alert alert-success py-2 mb-0">{msg}</div></div>}
      {err && <div className="col-12"><div className="alert alert-danger py-2 mb-0">{err}</div></div>}
      <div className="col-md-4">
        <input type="password" className="form-control" placeholder={t("profile.current_password")}
               name="old_password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)}
               data-testid="profile-oldpwd" />
      </div>
      <div className="col-md-3">
        <input type="password" className="form-control" placeholder={t("profile.new_password")} required minLength={6}
               name="new_password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
               data-testid="profile-newpwd" />
      </div>
      <div className="col-md-3">
        <input type="password" className="form-control" placeholder={t("profile.repeat_password")} required minLength={6}
               name="new_password2" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)}
               data-testid="profile-newpwd2" />
      </div>
      <div className="col-md-2">
        <button className="btn btn-outline-primary w-100" disabled={busy} data-testid="profile-changepwd">
          {busy ? "…" : t("profile.change")}
        </button>
      </div>
      <div className="col-12">
        <small className="text-muted">{t("profile.google_password_hint")}</small>
      </div>
    </form>
  );
}

function EcpBindSection() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const linked = !!user?.iin && !!user?.ecp_linked;

  const bind = async () => {
    setMsg(""); setErr(""); setBusy(true);
    try {
      const { challenge, challenge_id } = await ecpChallengeApi();
      const cms_b64 = await signChallengeWithEcp(challenge);
      const resp = await ecpBindApi(challenge_id, cms_b64);
      setMsg(`Привязано: ${resp.person_name} (ИИН ${resp.iin})`);
      if (refresh) await refresh();
    } catch (e) {
      setErr(normalizeApiError(e, ecpErrorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  if (linked) {
    return (
      <div data-testid="ecp-bind-linked">
        <div className="d-flex align-items-center gap-2 mb-2">
          <i className="bi bi-check-circle-fill text-success"></i>
          <span className="fw-semibold">ЭЦП привязана</span>
        </div>
        <div className="small text-muted">
          ИИН: <strong data-testid="ecp-bind-iin">{user.iin}</strong>
          {user.ecp_last_used && (
            <div>Последнее использование: {new Date(user.ecp_last_used).toLocaleString("ru-RU")}</div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mt-2"
          onClick={bind}
          disabled={busy}
          data-testid="ecp-rebind-btn"
        >
          {busy ? "Подписание…" : "Переподписать новым сертификатом"}
        </button>
        {msg && <div className="alert alert-success py-2 mb-0 mt-2 small" data-testid="ecp-bind-success">{msg}</div>}
        {err && <div className="alert alert-danger py-2 mb-0 mt-2 small" data-testid="ecp-bind-error">{err}</div>}
      </div>
    );
  }

  return (
    <div data-testid="ecp-bind-section">
      <div className="small text-muted mb-2">
        Привяжите ЭЦП НУЦ РК (RSA или ГОСТ) — и в следующий раз сможете войти в портал в один клик,
        без пароля. Также позволяет директорам автоматически создавать компании из БИН-сертификата.
      </div>
      {msg && <div className="alert alert-success py-2 mb-2 small" data-testid="ecp-bind-success">{msg}</div>}
      {err && <div className="alert alert-danger py-2 mb-2 small" data-testid="ecp-bind-error">{err}</div>}
      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={bind}
        disabled={busy}
        data-testid="ecp-bind-btn"
      >
        {busy ? (
          <><span className="spinner-border spinner-border-sm me-2" />Подписание…</>
        ) : (
          <><i className="bi bi-shield-lock me-1"></i>Привязать ЭЦП</>
        )}
      </button>
    </div>
  );
}

function LegalInfoSection() {
  const { t, i18n } = useTranslation();
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    getConsentMe().then(setConsent).catch(() => {});
  }, []);

  const locale = i18n.language === "kk" ? "kk-KZ" : i18n.language === "en" ? "en-GB" : "ru-RU";
  const dt = consent?.accepted_at
    ? new Date(consent.accepted_at).toLocaleString(locale, { dateStyle: "long", timeStyle: "short" })
    : null;

  return (
    <div data-testid="legal-info-section">
      {consent?.accepted ? (
        <div className="small text-success mb-2" data-testid="legal-consent-status">
          <i className="bi bi-check-circle me-1"></i>
          {t("legal.profile_accepted", { v: consent.accepted_version, d: dt })}
        </div>
      ) : consent ? (
        <div className="small text-muted mb-2" data-testid="legal-consent-status">
          {t("legal.profile_not_accepted")}
        </div>
      ) : null}
      <div className="d-flex flex-column gap-1 small">
        <Link to="/terms" target="_blank" data-testid="legal-link-terms">
          <i className="bi bi-box-arrow-up-right me-1"></i>{t("legal.terms_link")}
        </Link>
        <Link to="/privacy" target="_blank" data-testid="legal-link-privacy">
          <i className="bi bi-box-arrow-up-right me-1"></i>{t("legal.privacy_link")}
        </Link>
      </div>
    </div>
  );
}
